import { useBoolean, When } from '@teamsparta/react';
import { StackThemeProvider } from '@teamsparta/stack-core';
import { Flex } from '@teamsparta/stack-flex';
import { NavigationMore } from '@teamsparta/stack-icons';
import { vars } from '@teamsparta/stack-tokens';

import { useIsDesktop } from '@/hooks';
import { Dialog, Popover } from '@/pages/community/shared';
import { breakpoints } from '@/styles/themes';

import { DeleteButton, EditButton } from './components';

interface Props {
  deleteTitle?: string;
  deleteDescription?: string;
  isDeletePending: boolean;
  onDelete: () => void;
  onEdit: () => void;
}

export function Actions({
  deleteTitle,
  deleteDescription,
  isDeletePending,
  onDelete,
  onEdit,
}: Props) {
  const isDesktop = useIsDesktop({ breakpoint: breakpoints.largeTablet });

  const {
    value: dialogOpen,
    setTrue: openDialog,
    setFalse: closeDialog,
  } = useBoolean();
  const {
    value: popoverOpen,
    setTrue: openPopover,
    setFalse: closePopover,
  } = useBoolean();

  function handleEdit() {
    onEdit();
    closeDialog();
  }

  function handleDelete() {
    onDelete();
    closeDialog();
  }

  return (
    <When
      condition={isDesktop}
      fallback={
        <Dialog.Root
          open={dialogOpen}
          onOpenChange={(nextOpen) => (nextOpen ? openDialog() : closeDialog())}
        >
          <Dialog.Trigger>
            <NavigationMore size={16} color={vars.neutral[50]} />
          </Dialog.Trigger>
          <Dialog.Portal>
            <StackThemeProvider theme='hanghaeDark'>
              <Dialog.Overlay />
              <Dialog.Content>
                <Flex.Column gap={8} padding={24} fullWidth>
                  <EditButton onEdit={handleEdit} />
                  <DeleteButton
                    title={deleteTitle}
                    description={deleteDescription}
                    isPending={isDeletePending}
                    onDelete={handleDelete}
                  />
                </Flex.Column>
              </Dialog.Content>
            </StackThemeProvider>
          </Dialog.Portal>
        </Dialog.Root>
      }
    >
      <Popover.Root
        open={popoverOpen}
        onOpenChange={(nextOpen) => (nextOpen ? openPopover() : closePopover())}
      >
        <Popover.Trigger>
          <NavigationMore size={16} color={vars.neutral[50]} />
        </Popover.Trigger>
        <Popover.Portal>
          <StackThemeProvider theme='hanghaeDark'>
            <Popover.Content align='end'>
              <Flex.Column>
                <EditButton onEdit={handleEdit} />
                <DeleteButton
                  title={deleteTitle}
                  description={deleteDescription}
                  isPending={isDeletePending}
                  onDelete={handleDelete}
                />
              </Flex.Column>
            </Popover.Content>
          </StackThemeProvider>
        </Popover.Portal>
      </Popover.Root>
    </When>
  );
}
